import React, { useEffect, useMemo, useState } from 'react';
import { isIosDevice, isStandalone } from '../utils/pwa';

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [installOutcome, setInstallOutcome] = useState<'accepted' | 'dismissed' | null>(null);
  const [hasDismissed, setHasDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(isStandalone());

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      if (isInstalled || hasDismissed) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setCtaVisible(true);
      setInstallOutcome(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [hasDismissed, isInstalled]);

  useEffect(() => {
    const handleAppInstalled = () => {
      setInstallOutcome(null);
      setCtaVisible(false);
      setDeferredPrompt(null);
      setShowIosHelp(false);
      setIsInstalled(true);
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (isInstalled || hasDismissed) {
      setShowIosHelp(false);
      return;
    }

    if (isIosDevice() && !isStandalone()) {
      setShowIosHelp(true);
    }
  }, [hasDismissed, isInstalled]);

  useEffect(() => {
    if (!installOutcome) return;

    const timeoutId = window.setTimeout(() => setInstallOutcome(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [installOutcome]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;
    setInstallOutcome(choiceResult.outcome);
    setCtaVisible(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setCtaVisible(false);
    setShowIosHelp(false);
    setInstallOutcome(null);
    setHasDismissed(true);
    setDeferredPrompt(null);
  };

  const shouldRenderBanner = useMemo(
    () => !isInstalled && !hasDismissed && (ctaVisible || showIosHelp || Boolean(installOutcome)),
    [ctaVisible, showIosHelp, installOutcome, hasDismissed, isInstalled]
  );

  if (!shouldRenderBanner) {
    return null;
  }

  return (
    <div className="install-banner">
      <div className="install-banner__content">
        <div>
          <strong>Instala la aplicación</strong>
          {ctaVisible && (
            <p>
              Guarda la plataforma en tu pantalla de inicio para acceder más rápido y con
              experiencia de app.
            </p>
          )}
          {installOutcome && (
            <p>
              Preferencia registrada: {installOutcome === 'accepted' ? 'instalación iniciada' : 'instalación cancelada'}.
            </p>
          )}
          {showIosHelp && (
            <p className="install-banner__hint">
              En Safari para iOS toca <strong>Compartir</strong> y selecciona
              {' "Añadir a pantalla de inicio"'}.
            </p>
          )}
        </div>
        <div className="install-banner__actions">
          {ctaVisible && (
            <button type="button" className="install-banner__button" onClick={handleInstallClick}>
              Instalar
            </button>
          )}
          {(ctaVisible || showIosHelp) && (
            <button type="button" className="install-banner__link" onClick={handleDismiss}>
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
